import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import Panel from "@ui/panel/Panel";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { ruleCellText, ruleDateParts, ruleSummaryText, type RuleRecord } from "./ruleBlocksData";
import { RuleCheckSummary } from "./RuleCheckSummary";
import { RuleBlockParties } from "./RuleBlockParties";
import { EntityBanAction } from "../../bans/EntityBanAction";

const Content = styled.div`
	font: 13px/1.5 system-ui, sans-serif; color: #344054; min-width: 0; flex: 0 0 auto;
	.overview { margin-bottom: 20px; }

	.parties { margin: 16px 0 20px; padding: 14px 16px; background: #f8fafc; border: 1px solid #edf0f5; border-radius: 10px; }
	.party-link { display: block; width: fit-content; max-width: 100%; color: #167ca6; text-decoration: none; border-radius: 3px; }
	.party-link:hover strong { text-decoration: underline; }
	.party-link:focus-visible { outline: 2px solid #167ca6; outline-offset: 3px; }
	.overview-top { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; }
	.rule-name { margin: 0; font-size: 15px; font-weight: 600; line-height: 1.5; overflow-wrap: anywhere; }
	.outcome { padding: 3px 8px; background: #f2f4f7; border-radius: 5px; font-size: 12px; white-space: nowrap; }
	.outcome[data-outcome="blocked"], .outcome[data-outcome="check_failed"] { background: #fdf0f2; color: #b42342; }
	.outcome[data-outcome="allowed"] { background: #edf8f2; color: #18774d; }
	.entity { margin: 10px 0 5px; font-size: 20px; font-weight: 600; color: #167ca6; overflow-wrap: anywhere; }
	.event-meta { display: flex; flex-wrap: wrap; gap: 4px 14px; color: #667085; font-size: 12px; }
	.identifiers { margin-top: 20px; padding: 14px 16px; background: #f8fafc; border: 1px solid #edf0f5; border-radius: 10px; }
	.server-error { border-left: 3px solid #b42342; padding: 10px 12px; margin-top: 16px; background: #fff6f7; overflow-wrap: anywhere; }
	.server-error p { margin: 4px 0 0; }
	.full-record { margin-top: 18px; border-top: 1px solid #edf0f5; padding-top: 14px; }
	.full-record > summary { color: #667085; cursor: pointer; font-size: 12px; width: fit-content; }
	.full-record[open] > summary { margin-bottom: 14px; }
	dl { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px 20px; margin: 0; }
	dl > div { min-width: 0; }
	dt { color: #667085; font-size: 12px; margin-bottom: 4px; }
	dd { margin: 0; white-space: pre-wrap; overflow-wrap: anywhere; font-variant-numeric: tabular-nums; }
	.group { grid-column: 1 / -1; border-top: 1px solid #edf0f5; padding-top: 14px; margin-top: 4px; }
	.group > dt { font-size: 13px; font-weight: 600; color: #344054; margin-bottom: 10px; }
	.group .group { padding: 10px 12px; background: #f8fafc; border: 1px solid #edf0f5; border-radius: 8px; }
	pre { margin: 0; white-space: pre-wrap; overflow-wrap: anywhere; font: 12px/1.5 ui-monospace, monospace; }
	@media (max-width: 600px) { dl { grid-template-columns: minmax(0, 1fr); } }
`;

// Render every field, including unknown server fields, without placing payloads in the list.
const RecordFields = ({ value, depth = 0 }: { value: unknown; depth?: number }) => {
	const { t } = useTranslation();
	if (!value || typeof value !== "object") return <>{ruleCellText(value)}</>;
	if (depth >= 6) return <pre>{ruleCellText(value)}</pre>;
	const entries = Object.entries(value);
	if (!entries.length) return <>{Array.isArray(value) ? "[]" : "{}"}</>;
	return <dl>{entries.map(([key, item]) => <div key={key} className={item !== null && typeof item === "object" ? "group" : undefined}>
		<dt>{Array.isArray(value) ? `#${Number(key) + 1}` : t(`administration.fields.${key}`, { defaultValue: key.replaceAll("_", " ") })}</dt>
		<dd><RecordFields value={item} depth={depth + 1} /></dd>
	</div>)}</dl>;
};

export const RuleBlockDetails = ({ record, projectId, onNavigate }: { record: RuleRecord; projectId?: number; onNavigate?: () => void }) => {
	const { t, i18n } = useTranslation();
	const date = ruleDateParts(record.created_at, i18n.language);
	const identifiers = Object.fromEntries(["id", "client_id", "mid", "reference_id", "project_id", "sandbox"].filter(key => key in record).map(key => [key, record[key]]));
	const hasError = typeof record.error_message === "string" && !!record.error_message.trim() || record.error_code !== null && record.error_code !== undefined && record.error_code !== 0 && record.error_code !== "0" && record.error_code !== "";
	return <Content>
		<div className="overview">
			<div className="overview-top"><h2 className="rule-name">{ruleSummaryText(record.rule_name)}</h2><span className="outcome" data-outcome={ruleSummaryText(record.outcome)}>{t(`administration.outcome.${ruleSummaryText(record.outcome)}`, { defaultValue: ruleSummaryText(record.outcome) })}</span></div>
			<p className="entity">{ruleSummaryText(record.entity_value)}</p>
			<div className="event-meta"><span>{ruleSummaryText(record.entity_type)}</span><span>{date ? `${date.date} · ${date.time}` : ruleSummaryText(record.created_at)}</span><span>{t("administration.fields.action")}: {t(`administration.action.${ruleSummaryText(record.action)}`, { defaultValue: ruleSummaryText(record.action) })}</span></div>
		</div>
		<RuleBlockParties record={record} projectId={projectId} onNavigate={onNavigate} />
		<RuleCheckSummary record={record} />
		{hasError && <div className="server-error"><strong>{t("administration.checkError")}{record.error_code !== null && record.error_code !== undefined && ` · ${ruleSummaryText(record.error_code)}`}</strong><p>{ruleSummaryText(record.error_message)}</p></div>}
		{Object.keys(identifiers).length > 0 && <div className="identifiers"><RecordFields value={identifiers} /></div>}
		<details className="full-record"><summary>{t("administration.fullRecord")}</summary><RecordFields value={record} /></details>
	</Content>;
};

export const RuleBlockDetailsPopup = ({ record, projectId, userId, onClose }: { record: RuleRecord; projectId: number; userId: number; onClose: () => void }) => {
	const { t } = useTranslation();
	const popup = useRef<HTMLDivElement>(null);
	useEffect(() => {
		const previousFocus = document.activeElement;
		popup.current?.focus({ preventScroll: true });
		return () => { if (previousFocus instanceof HTMLElement && previousFocus.isConnected) previousFocus.focus({ preventScroll: true }); };
	}, []);
	if (typeof document === "undefined") return null;
	return createPortal(<div ref={popup} role="dialog" aria-modal="true" aria-label={t("administration.recordDetails")} tabIndex={-1}
		onKeyDown={event => {
			if (event.key === "Escape") { event.stopPropagation(); onClose(); return; }
			if (event.key !== "Tab") return;
			const buttons = popup.current?.querySelectorAll<HTMLElement>('button:not([disabled]), summary, [href], input:not([disabled]), select:not([disabled]), [tabindex="0"]');
			if (!buttons?.length) { event.preventDefault(); return; }
			const first = buttons[0], last = buttons[buttons.length - 1];
			if (event.shiftKey && (document.activeElement === first || document.activeElement === popup.current)) { event.preventDefault(); last.focus(); }
			else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
		}}>
		<Panel decorator="absolute" label={t("administration.recordDetails")} onClose={onClose} growContent sx={{ width: "820px", maxWidth: "94vw", height: "min(760px, 90dvh)", maxHeight: "90dvh", overflow: "hidden" }}>
			{(!record.project_id || Number(record.project_id) === projectId) && <div style={{ marginBottom: 12 }}><EntityBanAction type={record.entity_type} value={record.entity_value} projectId={projectId} userId={userId} /></div>}
			<RuleBlockDetails record={record} projectId={projectId} onNavigate={onClose} />
		</Panel>
	</div>, document.body);
};
